import type { RefMDEditor } from "@uiw/react-md-editor"
import React, {
  ForwardedRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useController } from "react-hook-form"

import classNames from "classnames"

import { useMergedRefs } from "shared/lib/useMergedRefs"
import { WithClassName } from "shared/types/WithClassName"
import { Expandable, ExpandableProps } from "shared/ui/expandable/Expandable"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { Input as DefaultInput, InputContext } from "../Input"
import { InputControlWrapper } from "../InputControlWrapper"
import { useInputDescriptionIDs } from "../accessibility/useInputAriaDescription"
import {
  TextInput,
  TextInputContext,
  TextInputRootProps
} from "../text/TextInput"
import {
  MarkdownEditor,
  MarkdownEditorProps
} from "./components/MarkdownEditor"
import { ReadonlyMarkdown } from "./components/ReadonlyMarkdown"
import "./styles/_markdown.scss"

const Root = React.forwardRef(
  (
    { className, ...props }: TextInputRootProps,
    ref?: React.ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <TextInput.Root
        {...props}
        ref={ref}
        className={classNames("markdown-input", className)}
      />
    )
  }
)

interface LoaderProps extends WithClassName {
  readonly?: boolean
}

function Loader({ readonly, className }: LoaderProps) {
  return (
    <Skeleton
      containerClassName={classNames(
        "input__loader",
        "input__control",
        { readonly },
        className
      )}
      count={6}
    />
  )
}

const InputBase = React.memo(
  React.forwardRef(
    (
      { className, textareaProps, ...props }: Partial<MarkdownEditorProps>,
      externalRef: ForwardedRef<HTMLTextAreaElement>
    ) => {
      const internalRef = useRef<RefMDEditor>(null)
      const { id, autoFocus, label } = useContext(InputContext)
      const { ariaDescribedBy } = useInputDescriptionIDs({
        additionalDescriptions: props["aria-describedby"]
      })

      const { inputRef } = useContext(InputContext)

      const ref = useMergedRefs(
        externalRef,
        inputRef as React.RefObject<HTMLTextAreaElement>
      )

      const mergedTextareaProps = useMemo(
        () => ({ ...textareaProps, id, "aria-label": label }),
        [textareaProps, label]
      )

      useEffect(() => {
        if (internalRef.current?.textarea) {
          ref(internalRef.current.textarea)
        }
      })

      return (
        <InputControlWrapper Loader={<Loader className={className} />}>
          <MarkdownEditor
            {...props}
            label={label || ""}
            ref={internalRef}
            textareaProps={mergedTextareaProps}
            autoFocus={autoFocus}
            aria-label={label}
            aria-describedby={ariaDescribedBy}
            className={classNames("input__control", className)}
          />
        </InputControlWrapper>
      )
    }
  )
)

const HookFormInput = React.memo(
  React.forwardRef(
    (
      { className, ...props }: Partial<MarkdownEditorProps>,
      externalRef: ForwardedRef<HTMLTextAreaElement>
    ) => {
      const internalRef = useRef<RefMDEditor>(null)
      const { name, required, disabled } = useContext(InputContext)

      const { minLength, maxLength } = useContext(TextInputContext)
      const {
        field: { ref: fieldRef, ...field },
        fieldState: { error }
      } = useController({
        name,
        disabled,
        rules: { minLength, maxLength, required }
      })

      const ref = useMergedRefs(externalRef, fieldRef)

      useEffect(() => {
        if (internalRef.current?.textarea) {
          ref(internalRef.current.textarea)
        }
      })

      return (
        <InputBase
          {...field}
          {...props}
          className={classNames({ error: !!error }, className)}
          ref={ref}
        />
      )
    }
  )
)

const Input = React.memo(
  React.forwardRef(
    (
      { className, ...props }: Partial<MarkdownEditorProps>,
      externalRef: ForwardedRef<HTMLTextAreaElement>
    ) => {
      const { name } = useContext(InputContext)

      if (name) {
        return <HookFormInput {...props} ref={externalRef} />
      }
      return <InputBase {...props} ref={externalRef} />
    }
  )
)

export interface MarkdownInputContainerProps
  extends WithClassName,
    Pick<
      ExpandableProps,
      "maxHeight" | "threshold" | "expandLabel" | "collapseLabel" | "onExpand"
    > {
  children: React.ReactNode
  /** If true, the container will expand when the input is focused */
  expandOnFocus?: boolean
}

export const MarkdownInputContainer = React.memo(
  ({
    children,
    maxHeight = null,
    threshold,
    expandLabel,
    collapseLabel,
    onExpand,
    className
  }: MarkdownInputContainerProps) => {
    const [isFocused, setIsFocused] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const expandButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
      if (!containerRef.current) return undefined

      const handleFocusIn = (event: FocusEvent) => {
        // Check if the focus is coming from the expand/collapse button
        const target = event.target as HTMLElement
        const isExpandButton =
          expandButtonRef.current &&
          (target === expandButtonRef.current ||
            expandButtonRef.current.contains(target))

        // Only auto-expand if focus is NOT from the expand/collapse button
        if (!isExpandButton) {
          setIsFocused(true)
          setIsExpanded(true)
        }
      }
      const handleFocusOut = () => setIsFocused(false)

      const container = containerRef.current
      container.addEventListener("focusin", handleFocusIn)
      container.addEventListener("focusout", handleFocusOut)

      return () => {
        container.removeEventListener("focusin", handleFocusIn)
        container.removeEventListener("focusout", handleFocusOut)
      }
    }, [])

    const handleExpand = (expanded: boolean) => {
      setIsExpanded(expanded)
      onExpand?.(expanded)
    }

    // Force expanded when focused and expandOnFocus is enabled
    const controlledExpanded = isFocused ? true : isExpanded

    return (
      <Expandable
        ref={containerRef}
        maxHeight={maxHeight}
        threshold={threshold}
        expandLabel={expandLabel}
        collapseLabel={collapseLabel}
        isExpanded={controlledExpanded}
        onExpand={handleExpand}
        buttonRef={expandButtonRef}
        className={classNames(
          "markdown-input__container",
          { "markdown-input__container--expandable": maxHeight !== null },
          className
        )}
      >
        {children}
      </Expandable>
    )
  }
)

const MarkdownInput = {
  ...DefaultInput,
  Root,
  Input,
  Readonly: ReadonlyMarkdown,
  Counter: TextInput.Counter,
  Container: MarkdownInputContainer
}

export { MarkdownInput }
